import { useState } from 'react';
import {
  CheckCircle,
  Clock,
  Pencil,
  Trash2,
  Copy,
  MoreVertical,
  ArrowUp,
  ArrowDown,
  Repeat,
  CreditCard,
} from 'lucide-react';
import { useAccounts } from '@hooks/useAccounts';
import { useCategories } from '@hooks/useCategories';
import Button from '@components/common/Button';
import { formatCurrency, formatDate } from '@utils/formatters';
import { clsx } from 'clsx';

const TransactionTable = ({
  items,
  dailyBalances,
  onEdit,
  onDelete,
  onDuplicate,
  onPayInstallment,
  onLaunchRecurrence,
  sortField = 'date',
  sortDirection = 'asc',
  onSort,
}) => {
  const { accounts } = useAccounts();
  const { categories } = useCategories();
  const [openMenuId, setOpenMenuId] = useState(null);

  const getAccountName = (accountId) => {
    const account = accounts.find(a => a.accountId === accountId);
    return account?.name || '';
  };

  const getCategoryInfo = (categoryId) => {
    const category = categories.find(c => c.categoryId === categoryId);
    return category || { name: 'Sem categoria', icon: '📁', color: '#6B7280' };
  };

  // Agrupar items por data e intercalar com saldo diário
  const groupedByDate = items.reduce((acc, item) => {
    const date = item.date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(item);
    return acc;
  }, {});

  // Ordenar datas
  const sortedDates = Object.keys(groupedByDate).sort((a, b) => {
    return sortDirection === 'asc' 
      ? a.localeCompare(b) 
      : b.localeCompare(a);
  });

  // Criar mapa de saldos por data
  const balanceByDate = dailyBalances?.reduce((acc, b) => {
    acc[b.date] = b.balance;
    return acc;
  }, {}) || {};

  const handleSort = (field) => {
    if (onSort) {
      const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
      onSort(field, newDirection);
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' 
      ? <ArrowUp size={14} className="ml-1" />
      : <ArrowDown size={14} className="ml-1" />;
  };

  const StatusIcon = ({ status, source }) => {
    if (status === 'completed') {
      return (
        <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
          <CheckCircle size={18} className="text-green-500" />
        </div>
      );
    }
    
    if (source === 'recurrence') {
      return (
        <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
          <Repeat size={18} className="text-purple-500" />
        </div>
      );
    }
    
    if (source === 'installment') {
      return (
        <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
          <CreditCard size={18} className="text-orange-500" />
        </div>
      );
    }

    return (
      <div className="w-8 h-8 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
        <Clock size={18} className="text-yellow-500" />
      </div>
    );
  };

  if (items.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
        <p className="text-gray-500 dark:text-gray-400">Nenhuma transação encontrada</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header da Tabela */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20">
                Situação
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 w-32"
                onClick={() => handleSort('date')}
              >
                <span className="flex items-center">
                  Data
                  <SortIcon field="date" />
                </span>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Descrição
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-40">
                Categoria
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32">
                Conta
              </th>
              <th 
                className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 w-36"
                onClick={() => handleSort('amount')}
              >
                <span className="flex items-center justify-end">
                  Valor
                  <SortIcon field="amount" />
                </span>
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {sortedDates.map((date) => {
              const dateItems = groupedByDate[date];
              const dayBalance = balanceByDate[date];
              
              return (
                <TransactionDateGroup
                  key={date}
                  date={date}
                  items={dateItems}
                  dayBalance={dayBalance}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onDuplicate={onDuplicate}
                  onPayInstallment={onPayInstallment}
                  onLaunchRecurrence={onLaunchRecurrence}
                  getAccountName={getAccountName}
                  getCategoryInfo={getCategoryInfo}
                  StatusIcon={StatusIcon}
                  openMenuId={openMenuId}
                  setOpenMenuId={setOpenMenuId}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Componente para grupo de transações de um dia
const TransactionDateGroup = ({
  date,
  items,
  dayBalance,
  onEdit,
  onDelete,
  onDuplicate,
  onPayInstallment,
  onLaunchRecurrence,
  getAccountName,
  getCategoryInfo,
  StatusIcon,
  openMenuId,
  setOpenMenuId,
}) => {
  const [launchingId, setLaunchingId] = useState(null);

  const handleLaunch = async (item) => {
    if (launchingId) return;
    setLaunchingId(item.id);
    try {
      await onLaunchRecurrence?.(item);
    } finally {
      setLaunchingId(null);
    }
  };

  return (
    <>
      {items.map((item, index) => {
        const category = getCategoryInfo(item.categoryId);
        const isIncome = item.type === 'income';
        const isPending = item.status === 'pending';
        
        return (
          <tr 
            key={item.id}
            className={clsx(
              'hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors',
              isPending && 'bg-yellow-50/50 dark:bg-yellow-900/10'
            )}
          >
            {/* Situação */}
            <td className="px-4 py-3">
              <StatusIcon status={item.status} source={item.source} />
            </td>

            {/* Data */}
            <td className="px-4 py-3">
              <span className={clsx(
                'text-sm',
                isPending ? 'text-gray-400' : 'text-gray-900 dark:text-white'
              )}>
                {formatDate(item.date)}
              </span>
            </td>

            {/* Descrição */}
            <td className="px-4 py-3">
              <div className="flex items-center gap-2">
                <span className={clsx(
                  'text-sm font-medium',
                  isPending ? 'text-gray-400' : 'text-gray-900 dark:text-white'
                )}>
                  {item.description}
                </span>
                {item.source === 'recurrence' && (
                  <span className="text-xs px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 rounded">
                    Recorrência
                  </span>
                )}
                {item.source === 'installment' && (
                  <span className="text-xs px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-600 rounded">
                    {item.installmentNumber}/{item.totalInstallments}
                  </span>
                )}
              </div>
            </td>

            {/* Categoria */}
            <td className="px-4 py-3">
              <div className="flex items-center gap-2">
                <span
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-sm"
                  style={{ backgroundColor: `${category.color}20` }}
                >
                  {category.icon}
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {category.name}
                </span>
              </div>
            </td>

            {/* Conta */}
            <td className="px-4 py-3">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {getAccountName(item.accountId)}
              </span>
            </td>

            {/* Valor */}
            <td className="px-4 py-3 text-right">
              <span className={clsx(
                'text-sm font-semibold',
                isIncome ? 'text-green-600' : 'text-red-600',
                isPending && 'opacity-60'
              )}>
                {formatCurrency(item.amount)}
              </span>
            </td>

            {/* Ações */}
            <td className="px-4 py-3">
              <div className="flex items-center justify-center gap-1">
                {item.source === 'transaction' && (
                  <>
                    <button
                      onClick={() => onEdit?.(item)}
                      className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      title="Editar"
                    >
                      <Pencil size={16} className="text-gray-400 hover:text-gray-600" />
                    </button>
                    <button
                      onClick={() => onDuplicate?.(item)}
                      className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      title="Duplicar"
                    >
                      <Copy size={16} className="text-gray-400 hover:text-gray-600" />
                    </button>
                    <button
                      onClick={() => onDelete?.(item)}
                      className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      title="Excluir"
                    >
                      <Trash2 size={16} className="text-gray-400 hover:text-red-500" />
                    </button>
                  </>
                )}
                {item.source === 'installment' && isPending && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => onPayInstallment?.(item)}
                  >
                    Pagar
                  </Button>
                )}
                {item.source === 'recurrence' && isPending && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleLaunch(item)}
                    isLoading={launchingId === item.id}
                    disabled={!!launchingId}
                  >
                    Lançar
                  </Button>
                )}
              </div>
            </td>
          </tr>
        );
      })}

      {/* Linha de Saldo do Dia */}
      {dayBalance !== undefined && (
        <tr className="bg-gray-100 dark:bg-gray-900/70">
          <td colSpan={5} className="px-4 py-2">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Saldo do dia
            </span>
          </td>
          <td className="px-4 py-2 text-right">
            <span className={clsx(
              'text-sm font-bold',
              dayBalance >= 0 ? 'text-gray-900 dark:text-white' : 'text-red-600'
            )}>
              {formatCurrency(dayBalance)}
            </span>
          </td>
          <td></td>
        </tr>
      )}
    </>
  );
};

export default TransactionTable;
